import { PerspectiveCamera, Vector3 } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { wuToMetres } from "../data/coords";
import type { Scene } from "../data/types";

/**
 * Build the free-look orbit camera + controls. Centred on the court's
 * geometric mid-point, framed to comfortably see the full rig.
 */
export function buildFreeLook(
  canvas: HTMLCanvasElement,
  sceneJson: Scene,
): {
  camera: PerspectiveCamera;
  controls: OrbitControls;
  resize(): void;
} {
  const camera = new PerspectiveCamera(
    50, canvas.clientWidth / canvas.clientHeight, 0.05, 200,
  );

  // Court centre in metres, in the Y-up frame (wuToMetres applies the
  // up-rotation + scale). For Y we use the authoritative floor height
  // derived from ankle Y by the exporter.
  const poly = sceneJson.court.polygon;
  const cxWu = poly.reduce((a, p) => a + p[0], 0) / poly.length;
  const czWu = poly.reduce((a, p) => a + p[1], 0) / poly.length;
  const [cx, , cz] = wuToMetres([cxWu, sceneJson.court.y, czWu]);
  const cy = sceneJson.court.floor_y_metres
    ?? wuToMetres([0, sceneJson.court.y, 0])[1];
  const target = new Vector3(cx, cy + 1.0, cz);  // 1 m above floor

  // Initial pose: outside the camera ring so the whole rig is visible. The
  // ring radius is derived from the calibrated camera positions so this
  // scales with whatever rig we're viewing.
  let maxDist = 0;
  for (const cam of sceneJson.cameras) {
    const [px, , pz] = wuToMetres(cam.C_world);
    const dx = px - cx;
    const dz = pz - cz;
    const d = Math.hypot(dx, dz);
    if (d > maxDist) maxDist = d;
  }
  const r = Math.max(8, maxDist * 1.8);   // safety floor + outside-ring margin
  camera.position.set(cx + r, cy + r * 0.5, cz + r);
  camera.lookAt(target);

  const controls = new OrbitControls(camera, canvas);
  controls.target.copy(target);
  controls.enableDamping = true;
  controls.dampingFactor = 0.1;
  controls.update();

  return {
    camera,
    controls,
    resize() {
      camera.aspect = canvas.clientWidth / canvas.clientHeight;
      camera.updateProjectionMatrix();
    },
  };
}
